import type { PlatformAdapter } from "./adapter";

/**
 * Adapter for local dev and itch.io grey-box builds. Ads are no-ops that
 * report completion so rewarded flows can be exercised; data is backed by
 * localStorage with an in-memory fallback for private-mode browsers.
 */
export function createLocalAdapter(): PlatformAdapter {
  const storage = safeLocalStorage() ?? memoryStorage();

  return {
    environment: "local",
    async init() {
      /* nothing to initialise locally */
    },
    ads: {
      async requestRewarded() {
        return { completed: true };
      },
      async requestMidgame() {
        /* no-op */
      },
    },
    data: {
      get: (key) => storage.getItem(key),
      set: (key, value) => storage.setItem(key, value),
      remove: (key) => storage.removeItem(key),
    },
    user: {
      isAvailable: () => false,
    },
    game: {
      gameplayStart() {},
      gameplayStop() {},
      loadingStart() {},
      loadingStop() {},
      happytime() {},
    },
  };
}

/** The part of `Storage` the adapter's data uses. */
type KeyValueStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

/** A storage that lives as long as the page: a private-mode browser's. */
function memoryStorage(): KeyValueStorage {
  const memory = new Map<string, string>();
  return {
    getItem: (key) => memory.get(key) ?? null,
    setItem: (key, value) => {
      memory.set(key, value);
    },
    removeItem: (key) => {
      memory.delete(key);
    },
  };
}

function safeLocalStorage(): Storage | null {
  try {
    const probeKey = "__probe__";
    globalThis.localStorage.setItem(probeKey, "1");
    globalThis.localStorage.removeItem(probeKey);
    return globalThis.localStorage;
  } catch {
    return null;
  }
}
